'use client';

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { feature } from 'topojson-client';
import {
  geoAlbersUsa,
  geoAzimuthalEqualArea,
  geoCentroid,
  geoContains,
  geoMercator,
  geoPath,
} from 'd3-geo';
import type { ExtendedFeature, GeoGeometryObjects, GeoPath, GeoProjection } from 'd3-geo';
import { getCountryMapPreset } from '@/lib/country-map-presets';
import type {
  CountryMapInset,
  CountryMapPreset,
  CountryMapProjectionConfig,
  CountryMapProjectionName,
} from '@/lib/country-map-presets';
import { getSubregionName, getSubregionsForCountry } from 'shared/src/region/admin1-registry';
import { countryFlagEmoji } from './country-flag';
import { fillRegionMapString } from '@/lib/ui-content/region-map-ui-content';
import type { RegionMapUiContent } from '@/lib/ui-content/region-map-ui-content';

/* ── geometry ─────────────────────────────────────────────────────────────
 * The generated admin-1 files carry exactly `{code, name}` per feature and a
 * single topology object (`admin1`). Presets were fitted against an 800x600
 * frame, so the SVG viewBox must stay 800x600 or every projection is wrong.
 */
const MAP_W = 800;
const MAP_H = 600;

export type Admin1Properties = { code: string; name: string };
type Admin1Feature = ExtendedFeature<GeoGeometryObjects, Admin1Properties>;
type Admin1Collection = { type: 'FeatureCollection'; features: Admin1Feature[] };

/* ── animation budget ──────────────────────────────────────────────────────
 * Subdivisions stagger in at ~12ms each, but the LAST one must still finish
 * inside 600ms — Japan (47) and the USA (51) would otherwise crawl for 900ms+.
 * The step therefore collapses as the count grows: 13 provinces keep the full
 * 12ms (404ms total), 47 and 51 compress to ~7ms and land on 600ms exactly.
 */
const ENTER_DURATION_MS = 260;
const ENTER_STEP_MS = 12;
const ENTER_BUDGET_MS = 600;
/** Labels come in once the shapes have essentially settled. */
const LABEL_LEAD_MS = 80;
const LABEL_FONT_SIZE = 11;
/** Inset frame labels sit a touch smaller than the shape labels they caption. */
const INSET_LABEL_FONT_SIZE = 10;
/**
 * The 800x600 viewBox is letterboxed into the container, so a phone renders
 * the type at roughly half size. Below this scale an 11px label is ~5px on
 * screen — worse than no label.
 */
const LABEL_MIN_RENDER_SCALE = 0.7;
/** Rough system-ui advance per character, as a fraction of the font size. */
const LABEL_CHAR_WIDTH_RATIO = 0.53;

/**
 * Countries whose subregion CODES are read as names by the people who live
 * there, so a code is a fair fallback when the full name will not fit.
 *
 * There is no general rule here and "alphabetic" is not one: it would happily
 * caption Chile with AI/AN/AP/AR, New Zealand with AUK/BOP/GIS, Argentina with
 * A/B/C and Ireland's Dublin with D — every bit as opaque as Japan's numeric
 * "47". Measured, this list is what turns the USA from 0 labels into 47
 * (38 by name, 9 by code) without inventing an abbreviation anyone has to
 * decode. Germany was the close call and was left out: BY and NW are known
 * domestically, MV and ST much less so.
 */
const CODE_LABEL_COUNTRIES = new Set(['us', 'ca', 'au', 'br', 'mx', 'in']);
const CODE_LABEL_PATTERN = /^[A-Za-z]{2,}$/;

function enterStepMs(count: number): number {
  if (count <= 1) return 0;
  return Math.min(ENTER_STEP_MS, (ENTER_BUDGET_MS - ENTER_DURATION_MS) / (count - 1));
}

/* ── palette ───────────────────────────────────────────────────────────────
 * Same teal-on-ocean the 3D globe uses (STATUS_COLORS.available #8dd4cd /
 * #00a798, OCEAN_COLOR #4a90e2) in both themes, so the cut from globe to
 * map reads as one continuous move. Labels stay dark on the mint land;
 * selected is brand #00a798 so the map and the side list agree.
 */
const PALETTE = {
  fill: '#8dd4cd',
  hover: '#4ec3b9',
  selected: '#00a798',
  stroke: '#ffffff',
  label: '#0d3b38',
  halo: 'rgba(255,255,255,0.9)',
  insetFill: 'rgba(255,255,255,0.14)',
  insetStroke: 'rgba(255,255,255,0.6)',
  insetLabel: '#ffffff',
  insetHalo: 'rgba(9,32,58,0.55)',
  backdrop: 'radial-gradient(120% 95% at 50% 38%, #5fa2e8 0%, #4a90e2 55%, #3b78c0 100%)',
} as const;

/* ── topojson cache ────────────────────────────────────────────────────────
 * One in-flight promise per country, shared by the prefetch that runs during
 * the ~900ms globe flight and by the component that mounts on arrival — so the
 * map is usually already parsed by the time it is asked for.
 */
const geometryCache = new Map<string, Promise<Admin1Collection>>();

type TopologyLike = Parameters<typeof feature>[0];
type TopoObjectLike = Parameters<typeof feature>[1];

async function loadCollection(preset: CountryMapPreset): Promise<Admin1Collection> {
  const response = await fetch(preset.geoUrl);
  if (!response.ok) throw new Error(`Map fetch failed: ${response.status}`);
  const topology = (await response.json()) as { objects?: Record<string, unknown> };
  const object = topology.objects?.[preset.topoObject];
  if (!object) throw new Error(`Topology object "${preset.topoObject}" missing`);
  return feature(topology as TopologyLike, object as TopoObjectLike) as unknown as Admin1Collection;
}

function ensureCollection(preset: CountryMapPreset): Promise<Admin1Collection> {
  const key = preset.geoUrl;
  const existing = geometryCache.get(key);
  if (existing) return existing;
  // A rejected fetch must not poison the cache for the next attempt.
  const pending = loadCollection(preset).catch((error: unknown) => {
    geometryCache.delete(key);
    throw error;
  });
  geometryCache.set(key, pending);
  return pending;
}

/**
 * Warm the geometry cache for a country — call it when the globe starts its
 * flight so the map is already parsed on arrival. No-op for a country with no
 * generated geometry; repeat calls join the first promise.
 */
export function prefetchCountryMap(countryCode: string): void {
  const preset = getCountryMapPreset(countryCode);
  if (!preset) return;
  // Swallow here: this is speculative work, and the real mount reports errors.
  void ensureCollection(preset).catch(() => undefined);
}

/* ── projection ────────────────────────────────────────────────────────────
 * Building the d3 projection here (rather than handing react-simple-maps a
 * projectionConfig, which cannot express `translate`) honours
 * `translate ?? [w/2, h/2]` for every country and lets the same instance place
 * shapes, labels and the pin.
 *
 * ⚠ CHANGE BOTH: scripts/lib/map-framing.mjs carries a literal transcription of
 * this function, and the generator asserts that replaying each emitted preset
 * through it reproduces the fitted bounds to within half a pixel. The call
 * ORDER is the contract.
 */
const PROJECTION_FACTORIES: Record<string, () => GeoProjection> = {
  geoMercator,
  geoAlbersUsa,
  geoAzimuthalEqualArea,
};

function buildProjection(
  name: CountryMapProjectionName,
  config: CountryMapProjectionConfig,
): GeoProjection {
  const factory = PROJECTION_FACTORIES[name] ?? geoMercator;
  const projection = factory();
  projection.scale(config.scale);
  projection.translate(config.translate ? [config.translate[0], config.translate[1]] : [MAP_W / 2, MAP_H / 2]);
  // geoAlbersUsa is a composite and exposes neither; the preset never sets them
  // for it, but guard anyway so a future preset edit cannot throw at runtime.
  if (config.center && typeof projection.center === 'function') {
    projection.center([config.center[0], config.center[1]]);
  }
  if (config.rotate && typeof projection.rotate === 'function') {
    projection.rotate([config.rotate[0], config.rotate[1], config.rotate[2]]);
  }
  return projection;
}

export type CountryMapPin = { lat: number; lng: number; label?: string | null };

export interface CountryMapViewProps {
  /** Internal country code (UK, not GB). */
  countryCode: string;
  /** Display name used in labels and aria text. */
  countryName: string;
  /** Language or full locale, for localized subregion names. */
  lang?: string;
  content: RegionMapUiContent;
  onSelectSubregion: (code: string, name: string) => void;
  onSelectWholeCountry: () => void;
  /** Subregion code to render as already chosen (Places / detect flows). */
  preselect?: string | null;
  /** Controlled hover, so the map and the side list mirror each other. */
  hoveredCode?: string | null;
  onHoverChange?: (code: string | null) => void;
  /** Marker at resolved coordinates. */
  pin?: CountryMapPin | null;
  /** Render the floating "All of {country}" pill. Off when a panel owns it. */
  showAllCountryAction?: boolean;
  reducedMotion?: boolean;
  className?: string;
}

export function CountryMapView({
  countryCode,
  countryName,
  lang,
  content,
  onSelectSubregion,
  onSelectWholeCountry,
  preselect = null,
  hoveredCode,
  onHoverChange,
  pin = null,
  showAllCountryAction = false,
  reducedMotion = false,
  className = '',
}: CountryMapViewProps) {
  const preset = useMemo(() => getCountryMapPreset(countryCode), [countryCode]);
  const clipPrefix = useId().replace(/:/g, '');

  /**
   * One projection for the main landmass, plus one per reprojected inset.
   * `composite` insets (geoAlbersUsa's Alaska and Hawaii) fall through to the
   * main projection, which has already relocated them.
   */
  const { mainProjection, projectionByCode, insetByCode } = useMemo(() => {
    const byCode = new Map<string, GeoProjection>();
    const insetOf = new Map<string, CountryMapInset>();
    if (!preset) return { mainProjection: null, projectionByCode: byCode, insetByCode: insetOf };
    const main = buildProjection(preset.projection, preset.projectionConfig);
    for (const inset of preset.insets) {
      for (const code of inset.codes) insetOf.set(code, inset);
      // 'composite' (geoAlbersUsa's Alaska and Hawaii) has no parts — the main
      // projection already relocated it. Otherwise every member carries its own
      // scale/translate so a shared box can size each territory independently.
      if (inset.kind === 'composite' || !inset.projection) {
        for (const code of inset.codes) byCode.set(code, main);
        continue;
      }
      for (const part of inset.parts) {
        byCode.set(part.code, buildProjection(inset.projection, part.projectionConfig));
      }
    }
    return { mainProjection: main, projectionByCode: byCode, insetByCode: insetOf };
  }, [preset]);

  const [collection, setCollection] = useState<Admin1Collection | null>(null);
  const [failed, setFailed] = useState(false);
  const [box, setBox] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [internalHover, setInternalHover] = useState<string | null>(null);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const hovered = hoveredCode !== undefined ? hoveredCode : internalHover;
  const setHovered = useCallback(
    (code: string | null) => {
      if (hoveredCode === undefined) setInternalHover(code);
      onHoverChange?.(code);
    },
    [hoveredCode, onHoverChange],
  );

  // Container box -> SVG letterbox math, so the HTML tooltip can sit on the
  // hovered shape whether it was reached by pointer or by keyboard.
  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return;
    const read = () => setBox({ w: node.clientWidth, h: node.clientHeight });
    read();
    const observer = new ResizeObserver(read);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!preset) return;
    let cancelled = false;
    setFailed(false);
    ensureCollection(preset)
      .then((data) => {
        if (!cancelled) setCollection(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [preset]);

  const registry = useMemo(() => getSubregionsForCountry(countryCode), [countryCode]);
  const registryCodes = useMemo(() => new Set(registry.map((unit) => unit.code)), [registry]);

  const displayName = useCallback(
    (code: string, fallback: string) => getSubregionName(countryCode, code, lang) ?? fallback,
    [countryCode, lang],
  );

  // Geometry the registry does not know about is a data bug, not a user-facing
  // one: the shape still renders, but nothing downstream can resolve it.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || !collection) return;
    const orphans = collection.features
      .map((f) => f.properties?.code)
      .filter((code): code is string => !!code && !registryCodes.has(code));
    if (orphans.length > 0) {
      console.warn(
        `[CountryMapView] ${countryCode}: geometry features with no admin1-registry entry: ${orphans.join(', ')}`,
      );
    }
  }, [collection, countryCode, registryCodes]);

  const features = useMemo(() => collection?.features ?? [], [collection]);
  const step = enterStepMs(features.length);
  const lastDelay = features.length > 1 ? step * (features.length - 1) : 0;

  const projFor = useCallback(
    (code: string) => projectionByCode.get(code) ?? mainProjection,
    [projectionByCode, mainProjection],
  );

  /** One geoPath per distinct projection, reused across shapes and labels. */
  const pathFor = useMemo(() => {
    const cache = new Map<GeoProjection, GeoPath>();
    return (projection: GeoProjection | null): GeoPath | null => {
      if (!projection) return null;
      let path = cache.get(projection);
      if (!path) {
        path = geoPath(projection);
        cache.set(projection, path);
      }
      return path;
    };
  }, []);

  /** Rendered `d` per code, each through its own projection. */
  const shapes = useMemo(() => {
    const rows: Array<{ code: string; name: string; d: string }> = [];
    for (const item of features) {
      const code = item.properties?.code;
      if (!code) continue;
      const path = pathFor(projFor(code));
      const d = path?.(item);
      if (!d) continue;
      rows.push({ code, name: item.properties?.name ?? code, d });
    }
    return rows;
  }, [features, pathFor, projFor]);

  /** Projected centroid + projected bounding box, in viewBox units, per code. */
  const geometryIndex = useMemo(() => {
    const map = new Map<string, { x: number; y: number; w: number; h: number }>();
    for (const item of features) {
      const code = item.properties?.code;
      if (!code) continue;
      const projection = projFor(code);
      const path = pathFor(projection);
      if (!projection || !path) continue;
      const [[x0, y0], [x1, y1]] = path.bounds(item);
      // Bounds centre for anything in a box: the Azores span five degrees of
      // ocean and their geographic centroid is open water between islands.
      // Also the fallback when a projection clips the centroid away — no
      // feature does today, but a Natural Earth bump must not silently drop a
      // tooltip anchor.
      const centroid = insetByCode.has(code) ? null : projection(geoCentroid(item));
      const point = centroid ?? [(x0 + x1) / 2, (y0 + y1) / 2];
      map.set(code, { x: point[0], y: point[1], w: x1 - x0, h: y1 - y0 });
    }
    return map;
  }, [features, pathFor, projFor, insetByCode]);

  /** How many container px one viewBox unit occupies (xMidYMid meet). */
  const renderScale = box.w && box.h ? Math.min(box.w / MAP_W, box.h / MAP_H) : 1;
  const showLabels = features.length > 0 && renderScale >= LABEL_MIN_RENDER_SCALE;
  const canUseCodeLabels = CODE_LABEL_COUNTRIES.has(countryCode.trim().toLowerCase());

  /**
   * The pin belongs to whichever inset actually contains it — a Canary Islands
   * address must land in the Canary Islands box, not on empty Atlantic in the
   * mainland frame.
   */
  const pinProjection = useMemo(() => {
    if (!pin || !preset || !mainProjection) return mainProjection;
    const point: [number, number] = [pin.lng, pin.lat];
    for (const inset of preset.insets) {
      if (inset.kind === 'composite') continue; // already relocated by the main projection
      for (const code of inset.codes) {
        const item = features.find((f) => f.properties?.code === code);
        if (!item || !geoContains(item as Parameters<typeof geoContains>[0], point)) continue;
        const projection = projectionByCode.get(code);
        const projected = projection?.(point);
        // Simplification can move a coastline; if the result escapes the box,
        // the mainland frame is the safer place to draw it.
        if (
          projected &&
          projected[0] >= inset.box[0] &&
          projected[0] <= inset.box[2] &&
          projected[1] >= inset.box[1] &&
          projected[1] <= inset.box[3]
        ) {
          return projection ?? mainProjection;
        }
      }
    }
    return mainProjection;
  }, [pin, preset, mainProjection, features, projectionByCode]);

  const pinPoint = useMemo(() => {
    if (!pinProjection || !pin) return null;
    const point = pinProjection([pin.lng, pin.lat]);
    return point ? { x: point[0], y: point[1] } : null;
  }, [pinProjection, pin]);

  /** viewBox units -> container px (xMidYMid meet letterboxing). */
  const toContainer = useCallback(
    (x: number, y: number): { left: number; top: number } | null => {
      if (!box.w || !box.h) return null;
      const scale = Math.min(box.w / MAP_W, box.h / MAP_H);
      return {
        left: (box.w - MAP_W * scale) / 2 + x * scale,
        top: (box.h - MAP_H * scale) / 2 + y * scale,
      };
    },
    [box],
  );

  const hoveredName = hovered ? displayName(hovered, hovered) : null;
  const tooltipAt = hovered ? geometryIndex.get(hovered) : undefined;
  const tooltipPos = tooltipAt ? toContainer(tooltipAt.x, tooltipAt.y) : null;

  const select = useCallback(
    (code: string, fallback: string) => onSelectSubregion(code, displayName(code, fallback)),
    [displayName, onSelectSubregion],
  );

  const insetLabel = useCallback(
    (inset: CountryMapInset) =>
      inset.codes.map((code) => displayName(code, inset.label)).join(' & '),
    [displayName],
  );

  const clipIdFor = useCallback(
    (inset: CountryMapInset) => `cmv-${clipPrefix}-${inset.codes.join('-')}`,
    [clipPrefix],
  );

  if (!preset) {
    // No generated geometry for this country — the caller still has the list.
    return null;
  }

  const enterClass = reducedMotion ? 'cmv-static' : 'cmv-enter';
  const insetDelay = Math.round(lastDelay + LABEL_LEAD_MS);

  const renderShape = (row: { code: string; name: string; d: string }, index: number) => {
    const name = displayName(row.code, row.name);
    const isSelected = !!preselect && preselect === row.code;
    const isHovered = hovered === row.code;
    const fill = isSelected ? PALETTE.selected : isHovered ? PALETTE.hover : PALETTE.fill;
    return (
      <g
        key={row.code}
        className={enterClass}
        style={{ animationDelay: `${Math.round(index * step)}ms` }}
      >
        <path
          d={row.d}
          role="button"
          tabIndex={0}
          aria-label={fillRegionMapString(content.subregionAriaLabel, { name, country: countryName })}
          aria-pressed={isSelected}
          onMouseEnter={() => setHovered(row.code)}
          onMouseLeave={() => setHovered(null)}
          onFocus={() => setHovered(row.code)}
          onBlur={() => setHovered(null)}
          onClick={() => select(row.code, row.name)}
          onKeyDown={(event: React.KeyboardEvent<SVGPathElement>) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            select(row.code, row.name);
          }}
          style={{
            fill,
            stroke: PALETTE.stroke,
            strokeWidth: isHovered || isSelected ? 1.1 : 0.6,
            outline: 'none',
            cursor: 'pointer',
            transition: 'fill 180ms ease-out, stroke-width 180ms ease-out',
          }}
        />
      </g>
    );
  };

  const mainShapes = shapes.filter((row) => !insetByCode.has(row.code));

  /**
   * Only draw a box we can actually fill.
   *
   * The frame and its label come from the preset (bundled JS); the geometry
   * comes from a separately cached fetch. If those ever disagree — a stale
   * `{cc}-admin1.json`, a half-finished regeneration — the naive version drew
   * the frame and caption with nothing inside, which reads as a broken map
   * rather than a missing one. `geoUrl` carries a content hash to stop the
   * mismatch happening at all; this makes the failure mode harmless anyway.
   */
  const drawableInsets = preset.insets.filter((inset) =>
    shapes.some((row) => inset.codes.includes(row.code)),
  );

  return (
    <div
      ref={wrapperRef}
      className={`relative h-full w-full overflow-hidden ${className}`}
      style={{ background: PALETTE.backdrop }}
    >
      <style>{CMV_CSS}</style>

      {!collection && !failed && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <div className="flex items-center gap-3 rounded-full bg-white/85 px-4 py-2 text-sm font-medium text-gray-700 shadow-lg backdrop-blur dark:bg-card/90 dark:text-ink">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-[#00a798]" />
            {content.loading}
          </div>
        </div>
      )}

      {failed && (
        <div className="absolute inset-x-4 top-4 z-10 rounded-xl bg-white/90 px-4 py-2.5 text-sm text-amber-700 shadow-lg backdrop-blur dark:bg-card/90 dark:text-amber-300">
          {content.loadError}
        </div>
      )}

      <svg
        viewBox={`0 0 ${MAP_W} ${MAP_H}`}
        preserveAspectRatio="xMidYMid meet"
        role="group"
        aria-label={fillRegionMapString(content.mapAriaLabel, { country: countryName })}
        style={{ width: '100%', height: '100%' }}
      >
        <defs>
          {drawableInsets.map((inset) => (
            <clipPath key={clipIdFor(inset)} id={clipIdFor(inset)}>
              <rect
                x={inset.box[0]}
                y={inset.box[1]}
                width={inset.box[2] - inset.box[0]}
                height={inset.box[3] - inset.box[1]}
                rx={6}
              />
            </clipPath>
          ))}
        </defs>

        {/* Inset frames go down BEFORE the shapes so the geometry sits on top,
            and never take pointer events — the shape inside owns hover. */}
        {drawableInsets.map((inset) => (
          <g
            key={`frame-${inset.codes.join('-')}`}
            className={enterClass}
            style={{ animationDelay: `${insetDelay}ms` }}
            pointerEvents="none"
          >
            <rect
              x={inset.box[0]}
              y={inset.box[1]}
              width={inset.box[2] - inset.box[0]}
              height={inset.box[3] - inset.box[1]}
              rx={6}
              fill={PALETTE.insetFill}
              stroke={PALETTE.insetStroke}
              strokeWidth={1}
              strokeDasharray="3 3"
            />
          </g>
        ))}

        {mainShapes.map(renderShape)}

        {/* Each territory is clipped to its own box: simplification can push a
            coastline a pixel past the frame it was fitted into. */}
        {drawableInsets.map((inset) => {
          const owned = shapes.filter((row) => inset.codes.includes(row.code));
          if (!owned.length) return null;
          return (
            <g key={`shapes-${inset.codes.join('-')}`} clipPath={`url(#${clipIdFor(inset)})`}>
              {owned.map(renderShape)}
            </g>
          );
        })}

        {/* Frame captions go last so they read over the geometry. aria-hidden:
            the shape inside already carries subregionAriaLabel, and without
            this a screen reader announces the territory twice. */}
        {drawableInsets.map((inset) => (
          <g
            key={`label-frame-${inset.codes.join('-')}`}
            className={enterClass}
            style={{ animationDelay: `${insetDelay}ms` }}
            pointerEvents="none"
          >
            <text
              x={inset.box[0] + 7}
              y={inset.box[3] - 6}
              aria-hidden
              style={{
                // Cartographic labels stay on the interface sans in both states
                // of `serif-typography`: at this size, bold and behind a 2.2px
                // halo, a serif fills in and stops being readable. `--font-ui`
                // says that on purpose, where the stack spelled out here before
                // just happened to be sans (app/globals.css).
                fontFamily: 'var(--font-ui)',
                fontSize: INSET_LABEL_FONT_SIZE,
                fontWeight: 700,
                fill: PALETTE.insetLabel,
                stroke: PALETTE.insetHalo,
                strokeWidth: 2.2,
                strokeLinejoin: 'round',
                paintOrder: 'stroke',
                userSelect: 'none',
              }}
            >
              {insetLabel(inset)}
            </text>
          </g>
        ))}

        {showLabels &&
          features.map((item) => {
            const code = item.properties?.code;
            const geom = code ? geometryIndex.get(code) : undefined;
            if (!code || !geom) return null;
            // A boxed territory is captioned by its frame.
            if (insetByCode.has(code)) return null;
            if (geom.h < LABEL_FONT_SIZE) return null;

            // Only label a shape the label actually fits inside. Without this
            // the Maritimes stack "New Brunswick"/"Nova Scotia"/"Prince Edward
            // Island" on top of each other. Where the name will not fit, the
            // subregion code often will — and for the countries listed in
            // CODE_LABEL_COUNTRIES that code is what locals call the place.
            const fits = (text: string) =>
              text.length * LABEL_FONT_SIZE * LABEL_CHAR_WIDTH_RATIO <= geom.w * 0.95;
            const name = displayName(code, item.properties?.name ?? code);
            let label: string | null = null;
            if (fits(name)) label = name;
            else if (canUseCodeLabels && CODE_LABEL_PATTERN.test(code) && fits(code)) {
              label = code.toUpperCase();
            }
            if (!label) return null;

            const active = hovered === code || preselect === code;
            return (
              // The placement transform MUST live on its own wrapper: the enter
              // animation animates the CSS `transform` property, and a CSS
              // transform wins over the SVG transform ATTRIBUTE — putting both
              // on one <g> parks every label at the SVG origin.
              <g key={`label-${code}`} transform={`translate(${geom.x}, ${geom.y})`}>
                <g
                  className={enterClass}
                  style={{ animationDelay: `${Math.round(lastDelay + LABEL_LEAD_MS)}ms` }}
                >
                  <text
                    textAnchor="middle"
                    dominantBaseline="central"
                    style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: LABEL_FONT_SIZE,
                      fontWeight: active ? 700 : 600,
                      fill: PALETTE.label,
                      stroke: PALETTE.halo,
                      strokeWidth: 2.2,
                      strokeLinejoin: 'round',
                      paintOrder: 'stroke',
                      pointerEvents: 'none',
                      userSelect: 'none',
                    }}
                  >
                    {label}
                  </text>
                </g>
              </g>
            );
          })}

        {pinPoint && (
          <g
            transform={`translate(${pinPoint.x}, ${pinPoint.y})`}
            aria-label={fillRegionMapString(content.pinAriaLabel, { label: pin?.label ?? countryName })}
          >
            {!reducedMotion && <circle className="cmv-pulse" r="6" fill="#ef4444" fillOpacity="0.35" />}
            <path
              d="M0,0 C0,0 7,-8 7,-13 C7,-17 3.9,-20 0,-20 C-3.9,-20 -7,-17 -7,-13 C-7,-8 0,0 0,0 Z"
              fill="#ef4444"
              stroke="#ffffff"
              strokeWidth="1.4"
            />
            <circle cx="0" cy="-13" r="2.6" fill="#ffffff" />
          </g>
        )}
      </svg>

      {/* Hover tooltip — anchored on the shape's centroid so pointer hover and
          keyboard focus produce the identical affordance. */}
      {hoveredName && tooltipPos && (
        <div
          className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-[130%] whitespace-nowrap rounded-lg bg-white/95 px-2.5 py-1.5 text-xs shadow-lg ring-1 ring-black/5 backdrop-blur dark:bg-card/95 dark:ring-hairline/50"
          style={{ left: tooltipPos.left, top: tooltipPos.top }}
        >
          <span className="block font-semibold text-gray-900 dark:text-ink">{hoveredName}</span>
          <span className="block text-[10px] text-gray-500 dark:text-muted">{content.selectHint}</span>
        </div>
      )}

      {/* Whole-country escape hatch, on the map itself. Off by default and off
          on mobile, where the sheet's card sits directly under a 38vh map. On
          desktop the panel's card is a whole column away from where the eye
          lands after the flight, so this is the same choice restated where the
          user is actually looking — hence the teal fill: it must read as the
          twin of the panel's primary card, not as a tertiary chrome pill.

          The generator reserves this footprint as a keep-out, so no inset box
          is ever placed underneath it. See KEEPOUTS in scripts/lib/map-framing.mjs. */}
      {showAllCountryAction && (
        <button
          type="button"
          onClick={onSelectWholeCountry}
          className="absolute bottom-3 left-1/2 z-20 inline-flex -translate-x-1/2 items-center gap-2 rounded-full bg-[#00a798] py-2 pl-2.5 pr-4 text-sm font-semibold text-white shadow-lg shadow-[#00a798]/30 transition-colors hover:bg-[#00937f]"
        >
          <span aria-hidden className="text-base leading-none">
            {countryFlagEmoji(countryCode)}
          </span>
          {fillRegionMapString(content.allOfCountry, { country: countryName })}
        </button>
      )}
    </div>
  );
}

/* Scoped keyframes. globals.css is owned by another workstream, and a
 * fill-mode:both animation (rather than a state-flip transition) means the
 * shapes are already at opacity 0 on their very first paint — no flash of a
 * fully drawn map before the stagger starts. */
const CMV_CSS = `
@keyframes cmvEnter {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
/* scale(), not r: animating the SVG geometry attribute via CSS is a newer
   capability than transform and silently no-ops on older Safari. The circle is
   drawn at the local origin, which is also its default transform-origin. */
@keyframes cmvPulse {
  0%   { transform: scale(0.55); opacity: 0.55; }
  70%  { transform: scale(2.4); opacity: 0; }
  100% { transform: scale(2.4); opacity: 0; }
}
.cmv-enter { animation: cmvEnter 260ms cubic-bezier(0.22, 1, 0.36, 1) both; }
.cmv-static { opacity: 1; }
.cmv-pulse { animation: cmvPulse 2s ease-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .cmv-enter { animation-duration: 1ms; animation-delay: 0ms !important; }
  .cmv-pulse { animation: none; }
}
`;

export default CountryMapView;
